import { motion } from "framer-motion";
import { ChevronLeft, Check, ShieldCheck } from "lucide-react";
import paywallGirl from "@/assets/paywall-main.webp";
import paywallBoy from "@/assets/paywall-main-boy.webp";
import { toGenitive, toAccusative } from "@/lib/name-utils";

interface Props {
  name: string;
  gender: "he" | "she" | "";
  onPay: () => void;
  onBack: () => void;
}

const Paywall = ({ name, gender, onPay, onBack }: Props) => {
  const genName = toGenitive(name);
  const accName = toAccusative(name);
  const isShe = gender === "she";
  const forHim = isShe ? "для неё" : "для него";
  const heShe = isShe ? "она" : "он";
  const himHer = isShe ? "ей" : "ему";
  const paywallImage = isShe ? paywallGirl : paywallBoy;

  const benefits = [
    `Персональную историю с именем ${genName}`,
    "Мягкую психологическую структуру",
    "Вопросы для обсуждения",
    "Небольшую подсказку для вас",
  ];

  return (
    <div className="min-h-screen flex flex-col items-center justify-start px-6 py-0 pb-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md space-y-8"
      >
        {/* 1. Full-width image with back button overlay */}
        <div className="relative -mx-6">
          <img
            src={paywallImage}
            alt="Обложка сказки"
            className="w-full rounded-b-3xl"
          />
          <button
            onClick={onBack}
            className="fixed top-4 left-4 z-50 w-9 h-9 rounded-full flex items-center justify-center text-foreground bg-card/70 backdrop-blur-sm hover:bg-card transition-colors"
            aria-label="Назад"
          >
            <ChevronLeft size={20} />
          </button>
        </div>

        {/* Title */}
        <div className="text-center space-y-2 px-2">
          <h2 className="text-2xl font-serif text-foreground">
            История для {genName} почти&nbsp;готова
          </h2>
          <p className="text-sm text-muted-foreground">
            Мы создали её специально {forHim}.
          </p>
        </div>

        {/* 2. Anticipation block */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="bg-card rounded-2xl p-6 shadow-sm border border-border text-center"
        >
          <p className="font-serif text-lg text-foreground mb-3">
            Представьте этот вечер
          </p>
          <p className="text-muted-foreground text-sm leading-relaxed">
            Сегодня вы укладываете {accName} спать
            и&nbsp;читаете историю, где {heShe}&nbsp;— главный герой.
            <br />
            Историю, которая помогает {himHer} стать спокойнее.
          </p>
        </motion.div>

        {/* 3. Benefits */}
        <div className="space-y-4">
          <h3 className="font-serif text-lg text-foreground text-center">
            Что вы получите
          </h3>
          <ul className="space-y-3">
            {benefits.map((b) => (
              <li key={b} className="flex items-start gap-3">
                <Check size={18} className="text-primary shrink-0" />
                <span className="text-sm text-foreground">{b}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* 4. Price */}
        <div className="text-center space-y-1">
          <p className="text-4xl font-sans font-bold text-foreground">149 ₽</p>
          <p className="text-sm text-muted-foreground">3 персональные истории</p>
        </div>

        {/* 5. CTA */}
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={onPay}
          className="w-full py-4 rounded-2xl bg-primary text-primary-foreground font-sans font-semibold text-lg shadow-lg hover:shadow-xl transition-shadow"
        >
          Получить историю для {genName}
        </motion.button>

        {/* 6. Guarantee */}
        <div className="flex items-start gap-2 justify-center">
          <ShieldCheck size={14} className="text-muted-foreground mt-0.5 shrink-0" />
          <p className="text-xs text-muted-foreground text-center leading-relaxed">
            Оплата безопасна. Если история не&nbsp;подойдёт&nbsp;— мы&nbsp;переделаем её&nbsp;бесплатно.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default Paywall;
